import React, { useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import { getActivePackagesByRoom, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { max } from 'lib/array/arrayUtils'
import MobileMultipleRoomsIndividualRoomOption from './MobileMultipleRoomsIndividualRoomOption'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextLink from 'components/Luxkit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import MobileMultipleRoomsIndividualRoomsFooter from './MobileMultipleRoomsIndividualRoomsFooter'
import Sheet from 'components/utils/Sheet'

const NoPackagesContainer = styled(Pane)`
  padding: ${rem(20)};
`

interface Props {
  rooms: Array<App.RoomOccupants>;
  checkIn: string;
  packageOptions: Array<OfferPackageOptionView>;
  multipleRoomPackages: PackageOptionViewByRoom;
}

function MobileMultipleRoomsIndividualRoomsContent(props: Props) {
  const { checkIn, rooms, multipleRoomPackages, packageOptions } = props

  const offerPageDispatch = useOfferPageDispatch()

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageOptions, checkIn])

  const onOpenCapacity = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch])

  return (
    <Sheet background="neutral-six" padding="0 0 8">
      <Group direction="vertical" gap={8}>
        {rooms.map((room, index) => {
          const packageView = multipleRoomPackages[room.roomId]
          if (!packageView) {
            return <NoPackagesContainer key={room.roomId}>
              <Group direction="vertical" gap={16}>
                <Group direction="vertical" gap={12}>
                  <StructureLabel variant="dark">Room {index + 1}</StructureLabel>
                  <div>
                    <Heading variant="heading4">No room available</Heading>
                    <BodyText variant="medium" colour="neutral-three">{formatOccupantsShort(room)}</BodyText>
                  </div>
                </Group>
                <MessageBanner
                  kind="warning"
                  density="compact"
                  description={<>
                    No room available please{' '}
                    <TextLink weight="regular" onClick={onOpenCapacity}>revise number of guests</TextLink>.
                  </>}
                />
              </Group>
            </NoPackagesContainer>
          }

          return <MobileMultipleRoomsIndividualRoomOption
            key={room.roomId}
            room={room}
            roomIndex={index}
            packageView={packageView}
            availableRoomCount={availableRoomCountByRoomId.get(room.roomId)}
          />
        })}
        <MobileMultipleRoomsIndividualRoomsFooter />
      </Group>
    </Sheet>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
}), MobileMultipleRoomsIndividualRoomsContent)
